import { useQuery } from "@tanstack/react-query";
import { Button, Modal, Table, Tabs, message } from "antd";
import TabPane from "antd/es/tabs/TabPane";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import LotteryDrawHistory from "components/admin/Member/LotteryDrawHistory";
import SurveyAnswersHistory from "components/admin/Member/SurveyAnswersHistory";
import VisitHistory from "components/admin/Member/VisitHistory";
import { motion } from "framer-motion";
import useAppSearchParams from "hooks/useAppSearchParams";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

/**
 * @typedef {'event' | 'campaign'} TabType
 *
 * @typedef {object} MemberGift
 * @property {string} name
 * @property {number} id
 *
 * @typedef {object} EventAndCampingAttendedModelProps
 * @property {boolean} open
 * @property {() => void} onCancel
 * @property {number} memberId
 *
 * @typedef {Object<string, string>} Note
 */

const StyleTable = styled(Table)`
	max-height: 60vh !important;

	.ant-table-container {
		max-height: 50vh !important;
		overflow-y: scroll;
	}

	.ant-table-content {
		min-width: 560px;
	}
`;

/**
 * @param {EventAndCampingAttendedModelProps} props
 * @returns {JSX.Element}
 */
const EventAndCampingAttendedModel = (props) => {
	const { open, onCancel, memberId, isUpdateTextAction = false } = props;
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useAppSearchParams({
		tab: COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.EVENT,
	});
	/** @type {TabType} */
	const currentTab = searchParams.tab;

	const columnEvent = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: `${COMMONS.PAGE_ADMIN_LIST_EVENTS}名`,
			dataIndex: "nameEvent",
			key: "nameEvent",
		},
		{
			title: `イベントプログラム名`,
			dataIndex: "nameProgram",
			key: "nameProgram",
			render: (value) => value || "－",
		},
		{
			title: "予約日付",
			dataIndex: "date",
			key: "date",
			render: (value, row) => {
				return (
					<>
						<p>{moment(value).format("YYYY-MM-DD")}</p>
						<p>{`${moment(row?.startAt).format("HH:mm")} ~ ${moment(
							row?.endAt,
						).format("HH:mm")}`}</p>
					</>
				);
			},
		},
		{
			title: "行動",
			dataIndex: "action",
			key: "action",
			render: (text) => {
				if (text === COMMONS.ACTION_CANCEL_EVENT && isUpdateTextAction)
					return COMMONS.TITLE_ACTION_CANCEL_EVENT;
				return text;
			},
		},
	];

	const { data: dataColumnEvent = [], isFetching: isFetchingEvents } = useQuery(
		[API.QUERY_KEY_ADMIN_HISTORY_REGISTRATIONS, memberId],
		() => API.ADMIN_GET_HISTORY_ATTENDED(memberId),
		{
			enabled:
				open &&
				!!memberId &&
				(!currentTab ||
					currentTab === COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.EVENT),
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			footer={null}
			width={720}
			destroyOnClose
			centered
			styles={{
				body: {
					maxHeight: "90vh",
				},
			}}
			afterClose={() => {
				setSearchParams(({ tab, ...prev }) => prev);
			}}
		>
			<Tabs
				defaultActiveKey={currentTab}
				className="mb-8"
				onChange={(key) => {
					setSearchParams((prev) => ({
						...prev,
						tab: key,
					}));
				}}
			>
				<TabPane
					tab="参加イベント履歴"
					key={COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.EVENT}
				>
					<StyleTable
						bordered
						size="small"
						className="w-full"
						dataSource={dataColumnEvent.map((data, rowKey) => ({
							...data,
							rowKey,
						}))}
						columns={columnEvent}
						loading={isFetchingEvents}
						rowKey="rowKey"
					/>
				</TabPane>
				<TabPane
					tab="アンケート回答履歴"
					key={COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.SURVEY_ANSWERS}
				>
					<SurveyAnswersHistory tabActiveKey={currentTab} memberId={memberId} />
				</TabPane>
				<TabPane
					tab="くじ引き履歴"
					key={COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.LOTTERY}
				>
					<LotteryDrawHistory tabActiveKey={currentTab} memberId={memberId} />
				</TabPane>
				<TabPane
					tab="来店履歴"
					key={COMMONS.MEMBER_ATTENDED_TAB_ACTIVE_KEY.VISIT}
				>
					<VisitHistory tabActiveKey={currentTab} memberId={memberId} />
				</TabPane>
			</Tabs>

			<motion.div
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
				initial="hidden"
				animate="show"
				exit="hidden"
			>
				<motion.div
					className="flex justify-center"
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				>
					<TapAnimationComponent>
						<Button
							size="large"
							className="px-12 text-black"
							onClick={onCancel}
						>
							閉じる
						</Button>
					</TapAnimationComponent>
				</motion.div>
			</motion.div>
		</Modal>
	);
};

export default EventAndCampingAttendedModel;
